import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import ConfirmModal from '../components/ConfirmModal';
import { promptsApi, postingIdeasApi } from '../services/api';

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString();
};

const EditIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

const ResetIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </svg>
);

const ResultsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const inputCls = 'w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white';
const labelCls = 'block text-sm font-medium text-gray-700 mb-1';

const PromptEditModal = ({ prompt, onClose, onSave, saving, error }) => {
  const [form, setForm] = useState({
    name: prompt?.name || '',
    prompt_text: prompt?.prompt_text || '',
    status: prompt?.status || 'new',
    model: prompt?.model || 'local',
    workflow_type: prompt?.workflow_type || 'content_creation',
  });

  useEffect(() => {
    setForm({
      name: prompt?.name || '',
      prompt_text: prompt?.prompt_text || '',
      status: prompt?.status || 'new',
      model: prompt?.model || 'local',
      workflow_type: prompt?.workflow_type || 'content_creation',
    });
  }, [prompt]);

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-4">
          {prompt ? 'Edit Prompt' : 'New Prompt'}
        </h3>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mb-4">
          <label className={labelCls}>Name</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            className={inputCls}
            placeholder="Prompt name"
          />
        </div>
        <div className="mb-4">
          <label className={labelCls}>Prompt text</label>
          <textarea
            value={form.prompt_text}
            onChange={(e) => setField('prompt_text', e.target.value)}
            rows={5}
            className={inputCls + ' resize-vertical'}
            placeholder="Write the prompt content here..."
          />
        </div>
        <div className="flex gap-4 mb-6">
          <div className="flex-1">
            <label className={labelCls}>Workflow</label>
            <select
              value={form.workflow_type}
              onChange={(e) => setField('workflow_type', e.target.value)}
              className={inputCls}
            >
              <option value="content_creation">content_creation</option>
              <option value="coding">coding</option>
            </select>
          </div>
          <div className="flex-1">
            <label className={labelCls}>Model</label>
            <select
              value={form.model}
              onChange={(e) => setField('model', e.target.value)}
              className={inputCls}
            >
              <option value="local">local</option>
              <option value="claude">claude</option>
              <option value="deepseek_pro">deepseek_pro</option>
              <option value="deepseek_flash">deepseek_flash</option>
            </select>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onSave(prompt?.promptid, form, !prompt?.promptid)}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-60 transition"
          >
            {saving ? 'Saving…' : prompt ? 'Save changes' : 'Create prompt'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const PostingIdeasModal = ({ onClose, onCreatePrompt }) => {
  const [postingIdeas, setPostingIdeas] = useState([]);
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingIdeas, setLoadingIdeas] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadPostingIdeas = async () => {
      try {
        const res = await postingIdeasApi.getAll();
        setPostingIdeas(res.data);
      } catch (err) {
        setError('Failed to load posting ideas');
      } finally {
        setLoading(false);
      }
    };
    loadPostingIdeas();
  }, []);

  const handleSelectBusiness = async (idea) => {
    setSelectedIdea(idea);
    setLoadingIdeas(true);
    setError('');
    try {
      const res = await postingIdeasApi.getIdeas(idea.id);
      const existing = res.data.ideas;
      if (existing && existing.length > 0) {
        setIdeas(existing);
      } else {
        await generateIdeas(idea);
      }
    } catch (err) {
      setError('Failed to load ideas');
    } finally {
      setLoadingIdeas(false);
    }
  };

  const generateIdeas = async (idea) => {
    setRegenerating(true);
    setError('');
    try {
      const res = await postingIdeasApi.generateIdeas((idea || selectedIdea).id);
      setIdeas(res.data.ideas);
    } catch (err) {
      setError('Failed to generate ideas');
    } finally {
      setRegenerating(false);
    }
  };

  const handleSelectIdea = (ideaText) => {
    onCreatePrompt({ prompt_text: ideaText, name: selectedIdea.name + ' - Generated', status: 'new' });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-4">Posting Ideas</h3>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : !selectedIdea ? (
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700 mb-3">Select a context</p>
            {postingIdeas.map((idea) => (
              <button
                key={idea.id}
                onClick={() => handleSelectBusiness(idea)}
                className="w-full text-left rounded-lg border border-gray-200 bg-white px-4 py-3 hover:bg-gray-50 transition text-sm text-gray-900"
              >
                {idea.name}
              </button>
            ))}
            <div className="pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (loadingIdeas || regenerating) ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mb-4"></div>
            <p className="text-sm text-gray-500">{regenerating ? 'Regenerating ideas…' : 'Loading ideas…'}</p>
          </div>
        ) : (
          <div>
            <p className="text-sm font-medium text-gray-700 mb-3">{selectedIdea.name}</p>
            <div className="space-y-2 mb-4">
              {ideas.map((idea, index) => (
                <button
                  key={index}
                  onClick={() => handleSelectIdea(idea)}
                  className="w-full text-left rounded-lg border border-gray-200 bg-white px-4 py-3 hover:bg-gray-50 transition text-sm text-gray-700"
                >
                  {idea}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => generateIdeas()}
                className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 transition"
              >
                Regenerate
              </button>
              <button
                type="button"
                onClick={() => { setSelectedIdea(null); setIdeas([]); }}
                className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50 transition"
              >
                Back
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Prompts = () => {
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletePromptId, setDeletePromptId] = useState(null);
  const [showPostingIdeasModal, setShowPostingIdeasModal] = useState(false);
  const [prefilledCreateData, setPrefilledCreateData] = useState(null);

  const loadPrompts = async () => {
    try {
      setLoading(true);
      const res = await promptsApi.getAll();
      setPrompts(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load prompts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPrompts();
  }, []);

  const closeModal = () => {
    setIsCreating(false);
    setEditingPrompt(null);
    setError('');
  };

  const handleSave = async (promptid, payload, isNew = false) => {
    setSaving(true);
    setError('');
    try {
      if (isNew) {
        await promptsApi.create(payload);
      } else {
        await promptsApi.update(promptid, payload);
      }
      await loadPrompts();
      closeModal();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (promptid) => {
    setError('');
    try {
      await promptsApi.delete(promptid);
      setDeletePromptId(null);
      await loadPrompts();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
  };

  const handleReset = async (promptid) => {
    try {
      await promptsApi.update(promptid, { status: 'new' });
      await loadPrompts();
    } catch (err) {
      setError(err.response?.data?.error || 'Reset failed');
    }
  };

  const statusBadge = (status) => {
    const normalized = (status || 'new').toLowerCase();
    const classes = {
      new: 'bg-yellow-100 text-yellow-700',
      processing: 'bg-blue-100 text-blue-700',
      review: 'bg-purple-100 text-purple-700',
      failed: 'bg-red-100 text-red-700',
    };
    return (
      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${classes[normalized] || 'bg-gray-100 text-gray-700'}`}>
        {status || 'new'}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Prompts</h1>
            <p className="mt-1 text-sm text-gray-500">Review and manage stored prompt entries.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={loadPrompts}
              title="Refresh"
              className="w-10 h-10 bg-gray-200 text-gray-600 rounded-full shadow-md hover:bg-gray-300 transition flex items-center justify-center"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setShowPostingIdeasModal(true)}
              title="Generate ideas"
              className="w-10 h-10 bg-purple-600 text-white rounded-full shadow-md hover:bg-purple-700 transition flex items-center justify-center"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m1.636 6.364l.707-.707M6.343 6.343l-.707-.707M12 21v-1m0-16a7 7 0 00-4.95 11.95A4.5 4.5 0 0012 20.5a4.5 4.5 0 004.95-3.55A7 7 0 0012 5z" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setIsCreating(true)}
              title="New prompt"
              className="w-10 h-10 bg-primary-600 text-white rounded-full shadow-md hover:bg-primary-700 transition flex items-center justify-center"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{error}</div>
        )}

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="grid grid-cols-8 gap-4 px-6 py-3 text-xs uppercase tracking-wide text-gray-500 border-b border-gray-100 hidden md:grid">
            <span className="col-span-1">Name</span>
            <span className="col-span-2">Prompt</span>
            <span className="col-span-1">Status</span>
            <span className="col-span-1">Workflow</span>
            <span className="col-span-1">Model</span>
            <span className="col-span-1">Created</span>
            <span className="col-span-1 text-right">Actions</span>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : prompts.length === 0 ? (
            <div className="px-6 py-14 text-center text-gray-500 text-sm">
              No prompts found. Add prompts directly in the database or via API.
            </div>
          ) : (
            prompts.map((prompt) => (
              <div key={prompt.promptid} className="grid grid-cols-1 gap-4 px-6 py-4 md:grid-cols-8 md:items-center border-b border-gray-100 last:border-0">
                <div className="md:col-span-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{prompt.name || 'Untitled'}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm text-gray-700 break-words line-clamp-2">{prompt.prompt_text || '—'}</p>
                </div>
                <div className="md:col-span-1">
                  {statusBadge(prompt.status)}
                </div>
                <div className="md:col-span-1">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${prompt.workflow_type === 'coding' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                    {prompt.workflow_type === 'coding' ? 'coding' : 'content'}
                  </span>
                </div>
                <div className="md:col-span-1">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${prompt.model === 'claude' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'}`}>
                    {prompt.model || 'local'}
                  </span>
                </div>
                <div className="md:col-span-1 text-sm text-gray-400">
                  {formatDate(prompt.created_at)}
                </div>
                <div className="md:col-span-1 flex items-center gap-1 md:justify-end">
                  {prompt.status === 'review' && (
                    <button
                      type="button"
                      onClick={() => navigate(`/prompts/${prompt.promptid}/results`)}
                      title="View results"
                      className="p-2 text-indigo-500 bg-indigo-50 hover:bg-indigo-100 rounded-md transition"
                    >
                      <ResultsIcon />
                    </button>
                  )}
                  {prompt.status === 'failed' && (
                    <button
                      type="button"
                      onClick={() => handleReset(prompt.promptid)}
                      title="Reset to new"
                      className="p-2 text-yellow-600 bg-yellow-50 hover:bg-yellow-100 rounded-md transition"
                    >
                      <ResetIcon />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setEditingPrompt(prompt)}
                    title="Edit prompt"
                    className="p-2 text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-md transition"
                  >
                    <EditIcon />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeletePromptId(prompt.promptid)}
                    title="Delete prompt"
                    className="p-2 text-red-400 bg-red-50 hover:bg-red-100 rounded-md transition"
                  >
                    <DeleteIcon />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {(isCreating || editingPrompt) && (
        <PromptEditModal
          prompt={editingPrompt || prefilledCreateData}
          error={error}
          saving={saving}
          onClose={() => {
            closeModal();
            setPrefilledCreateData(null);
          }}
          onSave={handleSave}
        />
      )}

      {deletePromptId && (
        <ConfirmModal
          message="Delete this prompt? This action cannot be undone."
          confirmLabel="Delete"
          variant="danger"
          onCancel={() => setDeletePromptId(null)}
          onConfirm={() => handleDelete(deletePromptId)}
        />
      )}

      {showPostingIdeasModal && (
        <PostingIdeasModal
          onClose={() => setShowPostingIdeasModal(false)}
          onCreatePrompt={(data) => {
            setPrefilledCreateData(data);
            setIsCreating(true);
            setShowPostingIdeasModal(false);
          }}
        />
      )}
    </div>
  );
};

export default Prompts;
